import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movieList } from "@/mocks/movies";
import { tvList } from "@/mocks/tvshows";
import { newsList } from "@/mocks/news";
import type { MediaItem, NewsItem } from "@/types";

const allMedia = [...(movieList as MediaItem[]), ...(tvList as MediaItem[])];
const news = newsList as NewsItem[];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || "";
  const [input, setInput] = useState(keyword);

  usePageMeta({
    title: keyword
      ? `搜索“${keyword}”-四虎视频`
      : "搜索影视-四虎视频",
    description: keyword
      ? `四虎视频搜索结果页，为你展示与“${keyword}”相关的电影、电视剧和影视资讯，帮助你快速定位想看的内容。`
      : "四虎视频搜索页面，支持按关键词检索电影、电视剧和影视资讯，快速找到你感兴趣的内容。",
    keywords: keyword
      ? `${keyword},影视搜索,电影搜索,电视剧搜索,四虎视频搜索`
      : "影视搜索,电影搜索,电视剧搜索,四虎视频搜索",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    if (kw) {
      setSearchParams({ keyword: kw });
    } else {
      setSearchParams({});
    }
  };

  const mediaResults = useMemo(() => {
    if (!keyword) return [];
    const kw = keyword.toLowerCase();
    return allMedia.filter(
      (m) =>
        m.title.toLowerCase().includes(kw) ||
        m.director.toLowerCase().includes(kw) ||
        m.region.toLowerCase().includes(kw) ||
        m.genres.some((g) => g.toLowerCase().includes(kw)) ||
        m.actors.some((a) => a.toLowerCase().includes(kw)) ||
        m.summary.toLowerCase().includes(kw),
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    const kw = keyword.toLowerCase();
    return news.filter(
      (n) =>
        n.title.toLowerCase().includes(kw) ||
        n.summary.toLowerCase().includes(kw) ||
        n.tags.some((t) => t.toLowerCase().includes(kw)),
    );
  }, [keyword]);

  const hotKeywords = ["科幻", "悬疑", "古装", "都市", "星海征途", "长夜将尽", "口碑"];

  const total = mediaResults.length + newsResults.length;
  const hasKeyword = keyword.trim().length > 0;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "搜索" }]} />

      <div className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          搜索影视内容
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入片名、演员、导演或类型关键词，快速查找电影、电视剧与影视资讯。
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-3 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-500" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="例如：科幻、星海征途、悬疑"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            {input && (
              <button
                type="button"
                aria-label="清空"
                onClick={() => setInput("")}
                className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line text-lg" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-8 py-3 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-search-line text-base" />
            搜索
          </button>
        </div>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-foreground-500">热门搜索：</span>
        {hotKeywords.map((kw) => (
          <button
            key={kw}
            type="button"
            onClick={() => {
              setInput(kw);
              setSearchParams({ keyword: kw });
            }}
            className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {kw}
          </button>
        ))}
      </div>

      {hasKeyword && (
        <p className="mt-6 text-sm text-foreground-600">
          关键词“<strong className="text-foreground-900">{keyword}</strong>”共找到{" "}
          <strong className="text-primary-700">{total}</strong> 条结果
        </p>
      )}

      {hasKeyword && total === 0 && (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
            没有找到相关内容
          </h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
            试试更简短或更常见的关键词，例如片名、演员姓名或“动作”“悬疑”等类型词，也可以浏览下方热门分类。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/category/movie"
              className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-primary-700"
            >
              浏览电影分类
            </Link>
            <Link
              to="/category/tv"
              className="rounded-md border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览电视剧分类
            </Link>
          </div>
        </div>
      )}

      {mediaResults.length > 0 && (
        <section className="mt-8">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-6 w-1.5 rounded-full bg-primary-500" />
            影视作品
            <span className="text-sm font-normal text-foreground-500">
              （{mediaResults.length}）
            </span>
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {mediaResults.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {newsResults.length > 0 && (
        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-6 w-1.5 rounded-full bg-accent-500" />
            影视资讯
            <span className="text-sm font-normal text-foreground-500">
              （{newsResults.length}）
            </span>
          </h2>
          <div className="mt-5 space-y-3">
            {newsResults.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-background-200/70 bg-background-50 px-4 py-3.5 transition-colors hover:border-primary-200 hover:bg-background-100/60"
              >
                <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-900">
                  {item.category}
                </span>
                <span className="flex-1 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                  {item.title}
                </span>
                <span className="text-xs text-foreground-500">{item.publishTime}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!hasKeyword && (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-14 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-line text-3xl" />
          </span>
          <p className="mt-5 text-sm text-foreground-600">
            输入关键词开始搜索，或点击上方热门搜索快速体验。
          </p>
        </div>
      )}
    </div>
  );
}